import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import PocketBase, { BaseAuthStore, type RecordModel } from "pocketbase";
import { pb, queryClient } from "@rekonas/data-access";

interface AuthContextValue {
  user: RecordModel | null;
  isAuthenticated: boolean;
  isRestoring: boolean;
  login: (identity: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function clearProtectedClientState(): void {
  void queryClient.cancelQueries();
  queryClient.clear();
}

function accessRevision(record: RecordModel | null): number {
  const value = record?.access_revision;
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<RecordModel | null>(() => pb.authStore.record);
  const [isRestoring, setIsRestoring] = useState(true);

  const generation = useRef(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    let alive = true;
    let refreshing: number | undefined;
    let principal = JSON.stringify([pb.authStore.record?.id, accessRevision(pb.authStore.record)]);

    const syncUser = () => {
      generation.current++;
      const next = JSON.stringify([pb.authStore.record?.id, accessRevision(pb.authStore.record)]);
      if (next !== principal) clearProtectedClientState();
      principal = next;
      if (alive) setUser(pb.authStore.isValid ? pb.authStore.record : null);
    };
    const unsubscribe = pb.authStore.onChange(syncUser);
    const invalidate = () => pb.authStore.clear();
    window.addEventListener("pb-auth-invalid", invalidate);

    async function refreshAccess() {
      if (!pb.authStore.isValid) return;
      const started = generation.current;
      if (refreshing === started) return;
      refreshing = started;
      // SDK auth methods save their result before resolving. Keep that write in
      // a private, in-memory store until this operation is still current.
      const client = new PocketBase(pb.baseURL, new BaseAuthStore());
      client.authStore.save(pb.authStore.token, pb.authStore.record);
      try {
        const result = await client.collection("users").authRefresh();
        if (alive && started === generation.current) {
          pb.authStore.save(result.token, result.record);
        }
      } catch {
        if (alive && started === generation.current) invalidate();
      } finally {
        if (refreshing === started) refreshing = undefined;
      }
    }

    async function restore() {
      if (!pb.authStore.isValid) pb.authStore.clear();
      else await refreshAccess();
      if (alive) setIsRestoring(false);
    }

    void restore();
    const interval = window.setInterval(() => { void refreshAccess(); }, 15_000);
    const onFocus = () => { void refreshAccess(); };
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      mounted.current = false;
      generation.current++;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      unsubscribe();
      window.removeEventListener("pb-auth-invalid", invalidate);
    };
  }, []);

  const login = useCallback(async (identity: string, password: string) => {
    pb.authStore.clear();
    const started = ++generation.current;
    const client = new PocketBase(pb.baseURL, new BaseAuthStore());
    const result = await client.collection("users").authWithPassword(identity.trim(), password);
    if (!mounted.current || started !== generation.current) {
      throw new DOMException("Authentication session changed", "AbortError");
    }
    pb.authStore.save(result.token, result.record);
  }, []);

  const logout = useCallback(() => {
    generation.current++;
    pb.authStore.clear();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: Boolean(user && pb.authStore.isValid), isRestoring, login, logout }),
    [user, isRestoring, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
